
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMovieContext } from "@/context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Movie } from "@/types/movie";

const EditMovie = () => {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const { allMovies, updateMovie } = useMovieContext();
  const { toast } = useToast();

  // Extract TMDB ID from movieId parameter (format: "tmdb_123" or just "123")
  const tmdbId = movieId?.startsWith('tmdb_') 
    ? parseInt(movieId.replace('tmdb_', ''))
    : parseInt(movieId || '0');

  // Find movie by TMDB ID
  const movie = allMovies.find(m => m.tmdbId === tmdbId);

  const [formData, setFormData] = useState<Partial<Movie>>({
    title: "",
    director: "",
    year: new Date().getFullYear(),
    tags: [],
    favorite: false,
    planToWatch: false,
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (movie) {
      setFormData({
        title: movie.title,
        director: movie.director,
        year: movie.year,
        tags: movie.tags || [],
        favorite: movie.favorite,
        planToWatch: movie.planToWatch,
      });
    }
  }, [movie]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!movie) {
      toast({
        title: "Error",
        description: "Movie not found",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const updatedMovie: Movie = {
        ...movie,
        ...formData,
        tmdbId: movie.tmdbId, // Ensure TMDB ID is preserved
      };

      await updateMovie(updatedMovie);
      
      toast({
        title: "Success",
        description: "Movie updated successfully",
      });
      
      navigate("/");
    } catch (error) {
      console.error("Error updating movie:", error);
      toast({
        title: "Error",
        description: "Failed to update movie",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof Movie, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagsChange = (value: string) => {
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    handleInputChange('tags', tags);
  };

  if (!movie) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-mono-eerieBlack mb-4">Movie Not Found</h1>
            <p className="text-mono-onyx mb-6">The movie you're trying to edit doesn't exist.</p>
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Movies
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="mr-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-mono-eerieBlack">Edit Movie</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                type="text"
                value={formData.title || ""}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="director">Director</Label>
              <Input
                id="director"
                type="text"
                value={formData.director || ""}
                onChange={(e) => handleInputChange('director', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                value={formData.year || ""}
                onChange={(e) => handleInputChange('year', parseInt(e.target.value) || new Date().getFullYear())}
                required
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                type="text"
                value={formData.tags?.join(', ') || ""}
                onChange={(e) => handleTagsChange(e.target.value)}
                placeholder="action, drama, thriller"
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="favorite"
                  checked={formData.favorite || false}
                  onCheckedChange={(checked) => handleInputChange('favorite', checked)}
                />
                <Label htmlFor="favorite">Mark as Gem</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="planToWatch"
                  checked={formData.planToWatch || false}
                  onCheckedChange={(checked) => handleInputChange('planToWatch', checked)}
                />
                <Label htmlFor="planToWatch">Add to Watchlist</Label>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate("/")}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMovie;
